import { useEffect, useRef, useState } from 'react'

type CascadeOption = {
  label?: string
  value?: string | number
  children?: CascadeOption[]
  [key: string]: any
}

type FieldNames = {
  value?: string
  label?: string
  children?: string
}

type Props = {
  options?: CascadeOption[]
  onChange?: (
    values: (string | number)[],
    selectedOptions: CascadeOption[]
  ) => void
  value?: (string | number)[] | any
  defaultValue?: (string | number)[] | any
  fieldNames?: FieldNames
  placeholder?: string
  disabled?: boolean
  allowClear?: boolean
  name?: string
  className?: string
  'data-testid'?: string
  // accepted for API compatibility, not used
  showSearch?: any
  getPopupContainer?: any
}

const getVal = (opt: CascadeOption, fn?: FieldNames) =>
  opt[fn?.value ?? 'value']

const getLabel = (opt: CascadeOption, fn?: FieldNames): string =>
  String(opt[fn?.label ?? 'label'] ?? '')

const getChildren = (
  opt: CascadeOption,
  fn?: FieldNames
): CascadeOption[] | undefined => opt[fn?.children ?? 'children']

const findOpt = (
  opts: CascadeOption[],
  val: string | number,
  fn?: FieldNames
) => opts.find((o) => String(getVal(o, fn)) === String(val))

const toPathArray = (val: any): (string | number)[] => {
  if (!val) {
    return []
  }
  if (Array.isArray(val)) {
    return val
  }
  return [val]
}

const resolvePathFromValues = (
  vals: (string | number)[],
  opts: CascadeOption[],
  fn?: FieldNames
): CascadeOption[] => {
  const path: CascadeOption[] = []
  let current = opts
  for (const val of vals) {
    const found = findOpt(current, val, fn)
    if (!found) {
      break
    }
    path.push(found)
    const children = getChildren(found, fn)
    if (!children?.length) {
      break
    }
    current = children
  }
  return path
}

// The folders of a selection path, i.e. the path without its leaf
const toFolderPath = (
  path: CascadeOption[],
  fn?: FieldNames
): CascadeOption[] => {
  const last = path[path.length - 1]
  if (last && !getChildren(last, fn)?.length) {
    return path.slice(0, -1)
  }
  return path
}

const CascadeSelect = ({
  options = [],
  onChange,
  value,
  defaultValue,
  fieldNames,
  placeholder = 'Please select',
  disabled = false,
  className,
  'data-testid': testId,
}: Props) => {
  const initialPath = resolvePathFromValues(
    toPathArray(value ?? defaultValue),
    options,
    fieldNames
  )

  const [open, setOpen] = useState(false)
  const [selectedPath, setSelectedPath] = useState<CascadeOption[]>(initialPath)
  // Folder stack the panel is currently drilled into
  const [navPath, setNavPath] = useState<CascadeOption[]>(
    toFolderPath(initialPath, fieldNames)
  )
  const containerRef = useRef<HTMLDivElement>(null)

  // Sync selectedPath when the controlled value changes externally (reset or initial load)
  const prevValueKeyRef = useRef(JSON.stringify(value))
  useEffect(() => {
    const key = JSON.stringify(value)
    if (prevValueKeyRef.current !== key) {
      prevValueKeyRef.current = key
      const newPath = resolvePathFromValues(
        toPathArray(value),
        options,
        fieldNames
      )
      setSelectedPath(newPath)
      if (!open) {
        setNavPath(toFolderPath(newPath, fieldNames))
      }
    }
  }, [value, options, fieldNames, open])

  // Close panel on outside click
  useEffect(() => {
    if (!open) {
      return
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const displayValue =
    selectedPath.length > 0
      ? selectedPath.map((o) => getLabel(o, fieldNames)).join(' / ')
      : ''

  const handleOpen = () => {
    if (disabled) {
      return
    }
    if (!open) {
      // Reopen in the folder of the current selection
      setNavPath(toFolderPath(selectedPath, fieldNames))
    }
    setOpen((prev) => !prev)
  }

  const handleBack = () => {
    setNavPath((prev) => prev.slice(0, -1))
  }

  const handleOptionClick = (opt: CascadeOption) => {
    if (getChildren(opt, fieldNames)?.length) {
      setNavPath((prev) => [...prev, opt])
      return
    }
    const newPath = [...navPath, opt]
    setSelectedPath(newPath)
    onChange?.(
      newPath.map((o) => getVal(o, fieldNames)),
      newPath
    )
    setOpen(false)
  }

  const currentLevelOptions =
    navPath.length > 0
      ? (getChildren(navPath[navPath.length - 1], fieldNames) ?? [])
      : options

  const breadcrumb = navPath.map((o) => getLabel(o, fieldNames)).join(' / ')

  return (
    <div
      ref={containerRef}
      className={`relative ${className ?? ''}`}
      data-testid={testId}
    >
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={handleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded bg-white text-left focus:outline-none focus:border-maroon-400 disabled:opacity-50 disabled:cursor-not-allowed min-h-10"
      >
        <span
          className={`truncate ${displayValue ? 'text-gray-900' : 'text-gray-400'}`}
          title={displayValue || undefined}
        >
          {displayValue || placeholder}
        </span>
        <span className="text-gray-500 text-xs ml-2 select-none">
          {open ? '∧' : '∨'}
        </span>
      </button>

      {/* Drill-down panel */}
      {open && (
        <div
          className="absolute z-50 w-full bg-white border border-gray-200 rounded shadow-lg mt-1"
          style={{ top: '100%', left: 0 }}
        >
          {navPath.length > 0 && (
            <button
              type="button"
              onClick={handleBack}
              title={breadcrumb}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-gray-900 border-b border-gray-200 bg-gray-50 rounded-t text-left focus:outline-none"
              data-testid={testId ? `${testId}-back` : undefined}
            >
              <span className="text-gray-500 flex-shrink-0">‹</span>
              <span className="truncate">{breadcrumb}</span>
            </button>
          )}
          <div className="py-1 overflow-y-auto max-h-70" role="listbox">
            {currentLevelOptions.map((opt) => {
              const optVal = String(getVal(opt, fieldNames))
              const selectedAtLevel = selectedPath[navPath.length]
              const isActive =
                selectedAtLevel !== undefined &&
                String(getVal(selectedAtLevel, fieldNames)) === optVal &&
                navPath.length === selectedPath.length - 1
              const hasChildren = !!getChildren(opt, fieldNames)?.length

              return (
                <div
                  key={optVal}
                  role="option"
                  aria-selected={isActive}
                  className={`px-3 py-1.5 text-sm cursor-pointer flex items-center justify-between gap-4 ${
                    isActive
                      ? 'bg-orange-50 font-semibold text-gray-900'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => handleOptionClick(opt)}
                >
                  <span className="truncate">{getLabel(opt, fieldNames)}</span>
                  {hasChildren && (
                    <span className="text-gray-400 text-xs flex-shrink-0">
                      ›
                    </span>
                  )}
                </div>
              )
            })}
            {currentLevelOptions.length === 0 && (
              <div className="px-3 py-1.5 text-sm text-gray-400">No items</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default CascadeSelect
